import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import UpdateDepartmentModal from "../Components/UpdateDepartmentModal";
import {
  ChevronLeft,
  ChevronRight,
  Building2,
  Network,
  Layers,
  CheckCircle2,
  XCircle,
  Wifi,
  Edit2,
  Search,
} from "lucide-react";
import Skeleton from "../Components/SkeletonLoader";

export default function DepartmentListPage() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("dbToken");
      const params = {
        pageNo,
        pageSize,
      };

      if (debouncedSearch) {
        params.searchParams = debouncedSearch;
      }

      await axios
        .get(`${server}/department/getAll`, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          if (response.data.success) {
            setDepartments(response.data.data);
            setTotalCount(response.data.count);
          } else {
            toast.error("Failed to fetch departments");
          }
        });
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast.error(
        error.response?.data?.message || "Error fetching departments",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPageNo(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchDepartments();
  }, [pageNo, pageSize, debouncedSearch]);

  const handleEditClick = (dept) => {
    setSelectedDept(dept);
    setIsEditModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    fetchDepartments();
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= Math.ceil(totalCount / pageSize)) {
      setPageNo(newPage);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && departments.length === 0) {
    return (
      <div className="p-8 flex items-center justify-center text-gray-500">
        <div className="space-y-4 w-full max-w-4xl">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header Section */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Department List
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage network departments and VLAN configurations.
            </p>
          </div>
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search departments..."
              className="pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400 w-full md:w-64"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-4">Department Info</th>
                <th className="px-6 py-4">Network Configuration</th>
                <th className="px-6 py-4">Hotspot Info</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Created At</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {departments.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-16 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="p-3 bg-gray-50 rounded-full">
                        <Building2 className="h-6 w-6 text-gray-300" />
                      </div>
                      <span className="text-sm font-medium">
                        No departments found
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                departments.map((dept) => (
                  <tr
                    key={dept.id}
                    className="group hover:bg-blue-50/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 shrink-0 border border-blue-200">
                          <Building2 className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 text-sm">
                            {dept.departmentName}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 rounded">
                              {dept.departmentSlug}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1.5 items-start">
                        <div className="flex items-center gap-2 text-sm text-gray-700 font-mono">
                          <Network className="h-3.5 w-3.5 text-gray-400" />
                          <span>{dept.iPv4Address}</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                          <Layers className="h-3 w-3" />
                          <span>VLAN: {dept.vlanId}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Wifi className="h-4 w-4 text-orange-500" />
                        <span className="font-medium">
                          {dept.hotspotName || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${!dept.deleted
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-rose-50 text-rose-700 border-rose-200"
                          }`}
                      >
                        {!dept.deleted ? (
                          <CheckCircle2 className="h-3 w-3" />
                        ) : (
                          <XCircle className="h-3 w-3" />
                        )}
                        {!dept.deleted ? "Active" : "Deleted"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 font-medium">
                        {dept.createdAt
                          ? new Date(dept.createdAt).toLocaleDateString()
                          : "—"}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {dept.createdAt
                          ? new Date(dept.createdAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                          : ""}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleEditClick(dept)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Edit Department"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalCount > 0 && (
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-xs font-medium text-gray-500">
              <span>
                Showing{" "}
                <span className="text-gray-900 font-bold">
                  {departments.length}
                </span>{" "}
                of <span className="text-gray-900 font-bold">{totalCount}</span>{" "}
                departments
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageNo(1);
                }}
                className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 cursor-pointer"
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pageNo - 1)}
                disabled={pageNo === 1}
                className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="px-4 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-mono font-medium text-gray-700 shadow-sm">
                Page {pageNo} of {totalPages || 1}
              </div>
              <button
                onClick={() => handlePageChange(pageNo + 1)}
                disabled={pageNo === totalPages || totalPages === 0}
                className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <UpdateDepartmentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        department={selectedDept}
        onSuccess={handleUpdateSuccess}
      />
    </div>
  );
}
